import { ExternalCalendarEvent } from '@griply/types';
import { delay } from '@griply/utils';
import { useRef } from 'react';

import { useDebounceValue } from './use-debounce-value';

export const externalCalendarEventsQueryKey = 'external-calendar-events';

const CHANGED_AT_DEBOUNCE_MS = 3000;
const CHANGED_AT_MAX_WAIT_MS = 15_000;
const FETCH_ATTEMPTS = 3;
const FETCH_RETRY_DELAY_MS = 1000;

type QueryFnContext = { signal?: { aborted: boolean } };

const fetchWithRetries = async <T>(
  run: () => Promise<T>,
  attemptsLeft: number,
  signal?: QueryFnContext['signal'],
): Promise<T> => {
  try {
    return await run();
  } catch (error) {
    if (attemptsLeft <= 1 || signal?.aborted) {
      throw error;
    }
    await delay(FETCH_RETRY_DELAY_MS);
    if (signal?.aborted) {
      throw error;
    }
    return fetchWithRetries(run, attemptsLeft - 1, signal);
  }
};

export type FetchExternalCalendarEvents = (input: {
  startDate: Date;
  endDate: Date;
  timezone: string;
}) => Promise<ExternalCalendarEvent[]>;

export type UseExternalCalendarEventsQueryConfigOptions = {
  startDate: string;
  endDate: string;
  timezone: string;
  changedAtMillis: number;
  fetchEvents: FetchExternalCalendarEvents;
  onFetchError: (error: unknown) => void;
};

type CreateQueryFnOptions = Omit<
  UseExternalCalendarEventsQueryConfigOptions,
  'changedAtMillis'
> & {
  lastSuccessfulData: { current: ExternalCalendarEvent[] };
};

// Deliberate parallel of web's createContainedQueryFn — cross-platform, and the fallback must survive key rotation (ref, not cache read).
export const createExternalCalendarEventsQueryFn =
  ({
    startDate,
    endDate,
    timezone,
    fetchEvents,
    onFetchError,
    lastSuccessfulData,
  }: CreateQueryFnOptions) =>
  async ({ signal }: QueryFnContext = {}): Promise<ExternalCalendarEvent[]> => {
    const attemptFetch = () =>
      fetchEvents({
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        timezone,
      });

    try {
      const data = await fetchWithRetries(attemptFetch, FETCH_ATTEMPTS, signal);
      if (!signal?.aborted) {
        lastSuccessfulData.current = data;
      }
      return data;
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }
      onFetchError(error);
      return lastSuccessfulData.current;
    }
  };

export const useExternalCalendarEventsQueryConfig = ({
  startDate,
  endDate,
  timezone,
  changedAtMillis,
  fetchEvents,
  onFetchError,
}: UseExternalCalendarEventsQueryConfigOptions) => {
  const debouncedChangedAt = useDebounceValue(
    changedAtMillis,
    CHANGED_AT_DEBOUNCE_MS,
    { maxWait: CHANGED_AT_MAX_WAIT_MS },
  );
  const lastSuccessfulData = useRef<ExternalCalendarEvent[]>([]);

  return {
    queryKey: [
      externalCalendarEventsQueryKey,
      startDate,
      endDate,
      timezone,
      debouncedChangedAt,
    ],
    queryFn: createExternalCalendarEventsQueryFn({
      startDate,
      endDate,
      timezone,
      fetchEvents,
      onFetchError,
      lastSuccessfulData,
    }),
    staleTime: Infinity,
    placeholderData: (previous?: ExternalCalendarEvent[]) => previous ?? [],
  };
};
